import { useEffect, useState, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, MapPin, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const empty = { name: "", address: "", notes: "" };

export default function FieldsPage() {
  const { activeTeam, canEdit } = useTeams();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const { data } = await api.get(`/teams/${activeTeam.id}/fields`);
    setItems(data);
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Nome campo richiesto");
      return;
    }
    const payload = { ...form, team_id: activeTeam.id };
    if (editingId) await api.put(`/fields/${editingId}`, payload);
    else await api.post(`/fields`, payload);
    toast.success("Campo salvato");
    setOpen(false);
    setForm(empty);
    setEditingId(null);
    load();
  };

  const handleNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const handleEdit = (f) => {
    setForm({ name: f.name, address: f.address || "", notes: f.notes || "" });
    setEditingId(f.id);
    setOpen(true);
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Eliminare "${f.name}"?`)) return;
    await api.delete(`/fields/${f.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="fields-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Campi sportivi</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Stadi & impianti</h1>
          <p className="text-slate-500">
            {items.length} {items.length === 1 ? "campo" : "campi"} salvati. Riutilizzabili nelle convocazioni.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Link to="/app/campi/calendario">
              <Button
                variant="outline"
                className="rounded-full"
                data-testid="field-calendar-link"
              >
                <CalendarDays size={16} className="mr-1" /> Calendario multi-squadra
              </Button>
            </Link>
            <Button
              onClick={handleNew}
              className="rounded-full bg-sky-500 hover:bg-sky-600"
              data-testid="new-field-btn"
            >
              <Plus size={16} className="mr-1" /> Nuovo
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((f) => (
          <Card key={f.id} className="p-5 border-slate-200" data-testid={`field-${f.id}`}>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <MapPin size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-lg">{f.name}</div>
                {f.address && <div className="text-sm text-slate-500">{f.address}</div>}
                {f.notes && <div className="text-xs text-slate-400 mt-1">{f.notes}</div>}
              </div>
              {canEdit && (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => handleEdit(f)}>
                    <Pencil size={15} />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(f)}>
                    <Trash2 size={15} className="text-red-500" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
            Nessun campo ancora. Aggiungi il primo per riutilizzarlo nelle convocazioni!
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica campo" : "Nuovo campo"}</DialogTitle>
          <DialogDescription className="sr-only">Inserisci o aggiorna i dati di un campo da gioco.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome campo</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Stadio Comunale"
                data-testid="field-name-input"
              />
            </div>
            <div>
              <Label>Indirizzo</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Via dello Sport 12, Milano"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Manto erboso, parcheggio gratuito, spogliatoi N° 3..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-field-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
